/**
 * Общий модуль реестра статусов работ (задача 122 §1.1).
 *
 * Тело реестра, тулбар (поиск + пилюли статусов) и host-логика (запрос,
 * фильтр-стейт, вывод состояния) вынесены из `WorkStatusPage`, чтобы выделенная
 * field-поверхность лида (`FieldWorkStatusPage`, `/field/lead/work-status`) и
 * десктопная таблица делили одно тело, а не копипастили derivation.
 *
 * `MobileRegistry` — карточная раскладка (телефон / узкая field-рамка);
 * `WorkStatusToolbar` — общий для карточек и десктопной таблицы;
 * `useWorkRegistryHost` — данные, фильтр и производное состояние.
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SearchInput } from "@/components/ui/search-input";
import { useWorkStatusRegistry } from "@/hooks/useWorkStatus";
import { WORK_STATUS_TONE } from "@/lib/badgeTones";
import { formatDate } from "@/lib/datetime";
import type { ProjectWorkStatus, RegistryProject, RegistryWork, WorkPair } from "@/api/workStatus";

/** Порядок пилюль фильтра — от начатого к завершённому, как читается работа. */
export const STATUS_FILTERS: ProjectWorkStatus[] = [
  "in_progress",
  "paused",
  "completed",
  "planned",
];

/**
 * Дефолт фильтра — незакрытые работы. Экран руководителя это список требующего
 * действия, а не архив: завершённые прячем, пока их не спросят явно.
 */
export const DEFAULT_STATUSES: ProjectWorkStatus[] = ["in_progress", "paused"];

export function isDefaultStatuses(statuses: ReadonlySet<ProjectWorkStatus>): boolean {
  return (
    statuses.size === DEFAULT_STATUSES.length && DEFAULT_STATUSES.every((s) => statuses.has(s))
  );
}

/**
 * Работы проекта. В сгенерированном контракте `works` опционален (у поля на
 * сервере `default_factory`), хотя ответ его всегда несёт — нормализуем в одной
 * точке, чтобы ниже по коду не расползались `?? []`.
 */
export function worksOf(project: RegistryProject): RegistryWork[] {
  return project.works ?? [];
}

/** Ключ строки — пара, а не id: у работы без overlay id ещё не существует. */
export function workKey(projectId: number, workTypeId: number): string {
  return `${projectId}:${workTypeId}`;
}

function matches(project: RegistryProject, work: RegistryWork, needle: string): boolean {
  if (needle === "") return true;
  const haystack = [
    work.work_type_name,
    (work.performers ?? []).map((performer) => performer.display_name).join(" "),
    project.project_name,
    project.project_code,
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(needle);
}

/**
 * Клиентский отбор реестра: поиск одним полем (работа / исполнитель / название и
 * номер проекта — руководитель не должен выбирать, «по чему» искать) плюс фильтр
 * статусов.
 *
 * Опустевшие группы выпадают целиком: заголовок проекта без строк — визуальный
 * мусор, а с фильтром статусов по умолчанию это срабатывает постоянно (проект,
 * у которого все работы завершены, из списка уходит целиком).
 */
export function filterRegistry(
  projects: RegistryProject[],
  rawQuery: string,
  statuses: ReadonlySet<ProjectWorkStatus>,
): RegistryProject[] {
  const needle = rawQuery.trim().toLowerCase();
  return projects
    .map((p) => ({
      ...p,
      works: worksOf(p).filter((w) => statuses.has(w.status) && matches(p, w, needle)),
    }))
    .filter((p) => worksOf(p).length > 0);
}

/** Производное состояние экрана — общее для карточек и таблицы. */
export type RegistryState = "pickUnit" | "loading" | "error" | "empty" | "emptySearch" | "ready";

export interface WorkRegistryHost {
  query: string;
  setQuery: (value: string) => void;
  statuses: Set<ProjectWorkStatus>;
  toggleStatus: (status: ProjectWorkStatus) => void;
  resetStatuses: () => void;
  isDefault: boolean;
  groups: RegistryProject[];
  totalWorks: number;
  narrowed: boolean;
  needsUnit: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
  state: RegistryState;
}

/**
 * Данные + фильтр + производное состояние реестра. Обе поверхности (десктопная
 * таблица и field-карточки) читают из него одно и то же — расхождений в
 * derivation между поверхностями быть не может.
 *
 * `role` нужен для двух гардов: admin гейтится ролью (его `acting_unit_ids`
 * пуст), и его реестр без юнита бэкенд отвергает 422 — не отправляем запрос,
 * пока подразделение не выбрано. Ждём и саму роль: до гидратации сессии `role`
 * ещё `undefined`, иначе admin на первом рендере считался бы не-админом и улетал
 * бы заведомо 422-й запрос.
 */
export function useWorkRegistryHost(
  unitId: number | null,
  includeChildren: boolean,
  role: string | undefined,
): WorkRegistryHost {
  const [query, setQuery] = useState("");
  const [statuses, setStatuses] = useState<Set<ProjectWorkStatus>>(new Set(DEFAULT_STATUSES));

  const isAdmin = role === "admin";
  const roleKnown = role != null;
  const needsUnit = isAdmin && unitId == null;
  const { data, isLoading, isError, error, refetch } = useWorkStatusRegistry(
    unitId,
    includeChildren,
    roleKnown && !needsUnit,
  );

  const groups = useMemo(
    () => filterRegistry(data?.projects ?? [], query, statuses),
    [data, query, statuses],
  );
  const totalWorks = groups.reduce((sum, p) => sum + worksOf(p).length, 0);
  const isDefault = isDefaultStatuses(statuses);
  const narrowed = query.trim() !== "" || !isDefault;

  const toggleStatus = (status: ProjectWorkStatus): void => {
    setStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };
  const resetStatuses = (): void => setStatuses(new Set(DEFAULT_STATUSES));

  const state: RegistryState = needsUnit
    ? "pickUnit"
    : isLoading || !roleKnown
      ? "loading"
      : isError
        ? "error"
        : totalWorks === 0
          ? narrowed
            ? "emptySearch"
            : "empty"
          : "ready";

  return {
    query,
    setQuery,
    statuses,
    toggleStatus,
    resetStatuses,
    isDefault,
    groups,
    totalWorks,
    narrowed,
    needsUnit,
    isLoading,
    isError,
    error,
    refetch: () => void refetch(),
    state,
  };
}

/**
 * Тулбар таблиц-списков: поиск + пилюли статусов, контролы без видимых лейблов
 * (образец — `ProjectsPage`). Общий для десктопной таблицы и field-карточек.
 */
export function WorkStatusToolbar({
  query,
  onQueryChange,
  statuses,
  onToggleStatus,
  onReset,
  isDefault,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  statuses: ReadonlySet<ProjectWorkStatus>;
  onToggleStatus: (status: ProjectWorkStatus) => void;
  onReset: () => void;
  isDefault: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <SearchInput
        containerClassName="max-w-xs"
        value={query}
        data-testid="work-status-search"
        aria-label={t("workStatus.search.aria")}
        placeholder={t("workStatus.search.placeholder")}
        onChange={(e) => onQueryChange(e.target.value)}
      />
      {STATUS_FILTERS.map((status) => {
        const active = statuses.has(status);
        const tone = WORK_STATUS_TONE[status];
        return (
          <button
            key={status}
            type="button"
            aria-pressed={active}
            data-testid={`work-status-filter-${status}`}
            onClick={() => onToggleStatus(status)}
            className={`rounded-full px-2 py-1 text-xs font-medium transition tone-${tone.tone} ${
              active ? "ring-foreground ring-2 ring-offset-1" : "opacity-70 hover:opacity-100"
            }`}
          >
            {t(`workStatus.status.${status}`)}
          </button>
        );
      })}
      {!isDefault && (
        <Button size="sm" variant="ghost" data-testid="work-status-filter-reset" onClick={onReset}>
          {t("workStatus.filters.reset")}
        </Button>
      )}
    </div>
  );
}

/**
 * Реестр на узком вьюпорте / выделенной field-поверхности (4a макапа) —
 * карточки вместо таблицы.
 *
 * Показывает ровно те же данные и тот же отфильтрованный список, что таблица:
 * это другая **раскладка** одного реестра, а не другой экран. Группировка по
 * проектам сохранена — без неё строки «Лазерная резка» из разных проектов
 * неразличимы.
 */
export function MobileRegistry({
  groups,
  state,
  selected,
  onSelect,
  locale,
  error,
}: {
  groups: RegistryProject[];
  state: RegistryState;
  selected: WorkPair | null;
  onSelect: (pair: WorkPair | null) => void;
  locale: string;
  error?: unknown;
}): React.ReactElement {
  const { t } = useTranslation();

  if (state !== "ready") {
    const message =
      state === "pickUnit"
        ? t("workStatus.pickUnit")
        : state === "loading"
          ? t("common.loading")
          : state === "error"
            ? null
            : state === "emptySearch"
              ? t("workStatus.emptySearch")
              : t("workStatus.empty");
    return (
      <div
        className="text-muted-foreground mt-4 rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)] px-4 py-8 text-center text-sm"
        data-testid="work-status-mobile-state"
      >
        {state === "error" ? (
          <ApiErrorNotice error={error} fallbackMessage={t("common.apiError.fallback")} />
        ) : (
          message
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-4" data-testid="work-status-mobile">
      {groups.map((project) => (
        <section key={project.project_id} className="flex flex-col gap-1.5">
          <h2 className="flex items-baseline gap-2 px-1">
            <span className="font-mono text-xs text-[var(--accent-tech)] tabular-nums">
              {project.project_code}
            </span>
            <span className="font-condensed text-[13px] font-medium tracking-[0.06em] uppercase">
              {project.project_name}
            </span>
          </h2>
          <ul className="flex flex-col gap-1.5">
            {worksOf(project).map((work) => {
              const key = workKey(project.project_id, work.work_type_id);
              const isSelected =
                selected?.project_id === project.project_id &&
                selected?.work_type_id === work.work_type_id;
              const tone = WORK_STATUS_TONE[work.status];
              return (
                <li key={key}>
                  <button
                    type="button"
                    data-testid={`work-status-card-${key}`}
                    aria-current={isSelected}
                    onClick={() =>
                      onSelect({
                        project_id: project.project_id,
                        work_type_id: work.work_type_id,
                      })
                    }
                    // Тач-таргет по density-токенам (задача 77 §2.1): карточка
                    // целиком — одна крупная цель, а не строка с мелкими зонами.
                    style={{ minHeight: "var(--entry-tap-secondary)" }}
                    className="flex w-full flex-col gap-1 rounded-[var(--radius-card)] border bg-[var(--bg-panel-0)] px-3 py-2.5 text-left"
                  >
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span className="flex-1 text-sm font-medium">{work.work_type_name}</span>
                      <Badge tone={tone.tone} emphasis={tone.emphasis}>
                        {t(`workStatus.status.${work.status}`)}
                      </Badge>
                      {work.overdue_days != null && (
                        <Badge
                          tone="danger"
                          emphasis="solid"
                          data-testid={`work-status-mobile-overdue-${key}`}
                        >
                          {t("workStatus.overdueBadge", { count: work.overdue_days })}
                        </Badge>
                      )}
                    </span>
                    <span className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
                      <span>
                        {(work.performers ?? []).length > 0
                          ? (work.performers ?? [])
                              .map((performer) => performer.display_name)
                              .join(", ")
                          : "—"}
                      </span>
                      {work.due_date != null && (
                        <span className="font-mono tabular-nums">
                          {t("workStatus.columns.dueDate")}: {formatDate(work.due_date, locale)}
                        </span>
                      )}
                      {work.forecast != null && (
                        <span className="font-mono tabular-nums">
                          {t(`workStatus.forecast.${work.forecast}`)}
                        </span>
                      )}
                    </span>
                    {(work.checkpoints ?? []).length > 0 && (
                      <span className="text-muted-foreground text-xs">
                        {t("workStatus.columns.checkpoints")}:{" "}
                        {(work.checkpoints ?? []).filter((c) => c.is_done).length}/
                        {(work.checkpoints ?? []).length}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
